import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ChevronLeft, ChevronRight, EventBusy, Refresh } from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { buildClassPalette, classColorTokens, colorForName, resolveClassColors } from '../../utils/classColors';
import SessionBookingsDrawer from './SessionBookingsDrawer';
import type { DrawerBooking } from './SessionBookingsDrawer';
import ScheduleClassFilter from './ScheduleClassFilter';
import type { ClassFilterValue } from './ScheduleClassFilter';
import ScheduleDayView from './ScheduleDayView';
import ScheduleTodayList from './ScheduleTodayList';
import type { BookingNotes } from './bookingRowBits';
import { notesModeFor, useUserNotes, UserNotePopover } from '../UserNoteDialog';
import SessionFormDialog from '../SessionFormDialog';
import { bookingStudioOf, studioIdOf } from '../../utils/studioOf';
import DeleteSessionDialog from '../DeleteSessionDialog';
import { SCHED, btnSx, btnSmSx, iconBtnSx, segBtnSx, segSx } from './scheduleTheme';

const IST_TIMEZONE = 'Asia/Kolkata';
const DAY_MS = 24 * 60 * 60 * 1000;

export interface ScheduleSession {
  _id: string;
  className: string;
  date: string;
  slots: number;
  availableSlots?: number;
  bookingCount?: number;
  price: number;
  duration: number;
  isLive?: boolean;
  image?: string;
  sessionType?: string;
  category?: 'workout' | 'wellness';
  class?: { _id: string; className?: string; color?: string | null } | string | null;
  instructor?: { _id: string; name: string; image?: string };
  classType?: { _id: string; title?: string; name?: string };
  studio?: any;
}

interface ScheduleCalendarViewProps {
  studioId: string;
  categoryFilter: 'workout' | 'wellness';
  /** Bumped by the page after any create / edit / delete / copy so this view refetches. */
  refreshKey: number;
  canWriteSessions: boolean;
  /** After an edit / delete / booking change made from the calendar. Omitted = the calendar just refetches itself. */
  onChanged?: () => void;
  /** Shown as "+ Add sessions" beside the day view. Omitted = no button. */
  onAddSessions?: () => void;
}

// ── IST calendar-day keys ('yyyy-MM-dd'), handled as UTC midnights so day maths
// never drifts with the viewer's own timezone.
const istKey = (d: Date) => formatInTimeZone(d, IST_TIMEZONE, 'yyyy-MM-dd');
const keyToUTC = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const utcToKey = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (key: string, n: number) => utcToKey(new Date(keyToUTC(key).getTime() + n * DAY_MS));
const mondayOf = (key: string) => addDays(key, -((keyToUTC(key).getUTCDay() + 6) % 7));
const fmtKey = (key: string, pattern: string) => formatInTimeZone(keyToUTC(key), 'UTC', pattern);

const classIdOf = (s: ScheduleSession) =>
  typeof s.class === 'string' ? s.class : s.class?._id || '';

const bookedOf = (s: ScheduleSession) => Math.max(0, s.slots - (s.availableSlots ?? s.slots));
const hasBooking = (s: ScheduleSession) => s.isLive !== false && bookedOf(s) > 0;

/** The one-line status shown on a session in every view. */
const sessionStatus = (s: ScheduleSession) => {
  const booked = bookedOf(s);
  if (s.isLive === false) return 'Cancelled';
  if ((s.availableSlots ?? 1) === 0) return `Full · ${booked}/${s.slots}`;
  return `${booked}/${s.slots} booked`;
};

type CalendarMode = 'today' | 'day' | 'week' | 'month';
const MODE_LABELS: Record<CalendarMode, string> = { today: "Today's list", day: 'Day', week: 'Week', month: 'Month' };

const ScheduleCalendarView: React.FC<ScheduleCalendarViewProps> = ({
  studioId,
  categoryFilter,
  refreshKey,
  canWriteSessions,
  onChanged,
  onAddSessions,
}) => {
  const { admin } = useAuth();
  const isInstructor = admin?.role === 'instructor';
  const instructorViewAllBookings: boolean =
    isInstructor && (admin?.studio as any)?.whitelabelConfig?.instructorViewAllBookings === true;

  const [mode, setMode] = useState<CalendarMode>('week');
  const [classFilter, setClassFilter] = useState<ClassFilterValue>('all');
  // Who is booked on the selected day, for Today's list and the Day view's name chips.
  const [bookingsBySession, setBookingsBySession] = useState<Record<string, DrawerBooking[]>>({});
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [waitingBySession, setWaitingBySession] = useState<Record<string, number> | null>(null);
  const [noteTarget, setNoteTarget] = useState<{
    _id: string;
    name?: string;
    studioId: string | null;
    studioLabel?: string;
    anchorEl: HTMLElement;
  } | null>(null);
  const [anchorKey, setAnchorKey] = useState<string>(() => istKey(new Date()));
  const [sessions, setSessions] = useState<ScheduleSession[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);
  // Edit / delete run the shared session dialogs right here, so the calendar
  // behaves the same on the Sessions page and the Bookings page.
  const [sessionToEdit, setSessionToEdit] = useState<ScheduleSession | null>(null);
  const [sessionToDelete, setSessionToDelete] = useState<ScheduleSession | null>(null);
  const afterChange = useCallback(() => {
    if (onChanged) onChanged();
    else setLocalRefresh((n) => n + 1);
  }, [onChanged]);
  const fetchIdRef = useRef(0);
  const bookingsFetchIdRef = useRef(0);
  const showsDay = mode === 'day' || mode === 'today';
  // Ticks every minute so the day view's "now" line keeps moving.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!showsDay) return;
    const id = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(id);
  }, [showsDay]);

  const todayKey = istKey(new Date());

  // Class ids belong to one studio, so a saved pick means nothing at another.
  useEffect(() => {
    setClassFilter('all');
  }, [studioId]);

  // Visible range: Mon–Sun for a week (the day views sit on a strip of that week);
  // whole Mon-started weeks covering the month.
  const { rangeStart, rangeEnd, monthKey } = useMemo(() => {
    if (mode !== 'month') {
      const start = mondayOf(anchorKey);
      return { rangeStart: start, rangeEnd: addDays(start, 6), monthKey: anchorKey.slice(0, 7) };
    }
    const first = `${anchorKey.slice(0, 7)}-01`;
    const firstUTC = keyToUTC(first);
    const last = utcToKey(new Date(Date.UTC(firstUTC.getUTCFullYear(), firstUTC.getUTCMonth() + 1, 0)));
    const start = mondayOf(first);
    const end = addDays(mondayOf(last), 6);
    return { rangeStart: start, rangeEnd: end, monthKey: anchorKey.slice(0, 7) };
  }, [mode, anchorKey]);

  const fetchSessions = useCallback(async () => {
    if (!studioId) return;
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      // Same call the list view uses — past and future, with booking counts.
      const res = await apiService.getStudioSessions(studioId, {
        startDate: rangeStart,
        endDate: rangeEnd,
        ...(categoryFilter === 'wellness' ? { category: 'wellness' } : {}),
      });
      if (fetchId !== fetchIdRef.current) return;
      setSessions(res.sessions || []);
    } catch (err: any) {
      if (fetchId !== fetchIdRef.current) return;
      setError(err.message || 'Failed to load sessions');
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, [studioId, rangeStart, rangeEnd, categoryFilter]);

  useEffect(() => {
    void fetchSessions();
  }, [fetchSessions, refreshKey, localRefresh]);

  // Classes drive the colours (picked in the class form, or auto-assigned).
  useEffect(() => {
    if (!studioId) return;
    apiService
      .getStudioClasses(studioId)
      .then((res: any) => setClasses(res.classes || []))
      .catch(() => setClasses([]));
  }, [studioId, refreshKey]);

  const colorByClass = useMemo(() => resolveClassColors(classes), [classes]);
  const fallbackPalette = useMemo(() => buildClassPalette(Math.max(classes.length, 1)), [classes.length]);
  const colorOf = useCallback(
    (s: ScheduleSession) => {
      const id = classIdOf(s);
      return colorByClass.get(id) || colorForName(s.className || id, fallbackPalette);
    },
    [colorByClass, fallbackPalette],
  );

  const visibleSessions = useMemo(() => {
    if (classFilter === 'all') return sessions;
    if (classFilter === 'booked') return sessions.filter(hasBooking);
    return sessions.filter((s) => (classIdOf(s) || s.className) === classFilter.classKey);
  }, [sessions, classFilter]);

  const sessionsByDay = useMemo(() => {
    const map = new Map<string, ScheduleSession[]>();
    visibleSessions.forEach((s) => {
      const key = istKey(new Date(s.date));
      map.set(key, [...(map.get(key) || []), s]);
    });
    map.forEach((list) => list.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()));
    return map;
  }, [visibleSessions]);

  // Summary counts only what's on screen (the week, or the month itself).
  const inPeriod = useCallback(
    (s: ScheduleSession) => mode !== 'month' || istKey(new Date(s.date)).slice(0, 7) === monthKey,
    [mode, monthKey],
  );
  const inScope = useMemo(() => visibleSessions.filter(inPeriod), [visibleSessions, inPeriod]);
  const liveInScope = inScope.filter((s) => s.isLive !== false);
  const totalSlots = liveInScope.reduce((n, s) => n + (s.slots || 0), 0);
  const totalBooked = liveInScope.reduce((n, s) => n + bookedOf(s), 0);
  const fillPct = totalSlots > 0 ? Math.round((totalBooked / totalSlots) * 100) : 0;

  // The filter's counts ignore the filter itself, so every option shows its real total.
  const periodSessions = useMemo(() => sessions.filter(inPeriod), [sessions, inPeriod]);
  const classOptions = useMemo(() => {
    const seen = new Map<string, { key: string; name: string; color: string; count: number }>();
    periodSessions.forEach((s) => {
      const key = classIdOf(s) || s.className;
      const row = seen.get(key) || { key, name: s.className, color: classColorTokens(colorOf(s)).accent, count: 0 };
      seen.set(key, { ...row, count: row.count + 1 });
    });
    // A picked class stays listed in a period where it has no sessions.
    if (typeof classFilter === 'object' && !seen.has(classFilter.classKey)) {
      const picked = classes.find((c) => c._id === classFilter.classKey);
      const key = classFilter.classKey;
      seen.set(key, { key, name: picked?.className || key, color: colorByClass.has(key) ? classColorTokens(colorByClass.get(key)!).accent : SCHED.muted, count: 0 });
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [periodSessions, colorOf, classFilter, classes, colorByClass]);

  const selectedSession = sessions.find((s) => s._id === selectedSessionId) || null;

  const daySessions = useMemo(() => sessionsByDay.get(anchorKey) || [], [sessionsByDay, anchorKey]);

  // The list endpoint narrows bookings by session, not by the day they're on, so
  // the day is read one session at a time. Stable string so a refetch of the same
  // sessions doesn't refire this on its own; refresh bumps do that deliberately.
  // The endpoint ignores instructorId too, so a limited instructor's own classes
  // are enforced by only asking for those.
  const limitedInstructor = isInstructor && !instructorViewAllBookings;
  const dayBookingIds = useMemo(
    () =>
      daySessions
        .filter((s) => s.isLive !== false && (!limitedInstructor || s.instructor?._id === admin?.instructorId))
        .map((s) => s._id)
        .join(','),
    [daySessions, limitedInstructor, admin?.instructorId],
  );
  useEffect(() => {
    if (!showsDay || !dayBookingIds) return;
    const fetchId = ++bookingsFetchIdRef.current;
    setBookingsLoading(true);
    Promise.all(
      dayBookingIds.split(',').map(async (sessionId) => {
        const res: any = await apiService.getBookings({ sessionId, page: 1, pageSize: 500 });
        return [sessionId, res?.bookings || []] as const;
      }),
    )
      .then((rows) => {
        if (fetchId !== bookingsFetchIdRef.current) return;
        setBookingsBySession((prev) => ({ ...prev, ...Object.fromEntries(rows) }));
      })
      .catch((err: any) => {
        if (fetchId === bookingsFetchIdRef.current) setError(err.message || 'Failed to load bookings');
      })
      .finally(() => {
        if (fetchId === bookingsFetchIdRef.current) setBookingsLoading(false);
      });
  }, [showsDay, dayBookingIds, refreshKey, localRefresh]);

  // One set of member notes for Today's list and the Day view, so an edit shows in both.
  const notesMode = notesModeFor(admin?.role);
  const noteUserIds = useMemo(
    () => Object.values(bookingsBySession).flat().map((b) => b.user?._id),
    [bookingsBySession],
  );
  const { notesByUser, updateNote } = useUserNotes(noteUserIds, notesMode);
  const notes: BookingNotes = {
    mode: notesMode,
    notesByUser,
    open: (anchorEl, booking) => {
      if (!booking.user?._id) return;
      setNoteTarget({
        _id: booking.user._id,
        name: booking.user.name,
        studioId: bookingStudioOf(booking),
        studioLabel: booking.studio?.studioName,
        anchorEl,
      });
    },
  };

  // Waitlist counts are a nice-to-have — any failure just shows no number.
  useEffect(() => {
    if (!showsDay) return;
    let cancelled = false;
    apiService
      .getSessionWaitlists(anchorKey)
      .then((res: any) => {
        if (cancelled) return;
        setWaitingBySession(Object.fromEntries((res?.sessions || []).map((row: any) => [row._id, row.waitingCount ?? 0])));
      })
      .catch(() => {
        if (!cancelled) setWaitingBySession(null);
      });
    return () => {
      cancelled = true;
    };
  }, [showsDay, anchorKey, refreshKey, localRefresh]);

  const shift = (dir: -1 | 1) => {
    if (mode !== 'month') {
      setAnchorKey((k) => addDays(k, dir * 7));
    } else {
      setAnchorKey((k) => {
        const d = keyToUTC(`${k.slice(0, 7)}-01`);
        return utcToKey(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + dir, 1)));
      });
    }
  };

  const title =
    mode !== 'month'
      ? `${fmtKey(rangeStart, 'd MMMM')} to ${fmtKey(rangeEnd, 'd MMMM')}`
      : fmtKey(`${monthKey}-01`, 'MMMM yyyy');

  const openSession = (id: string) => setSelectedSessionId(id);

  const handleBookingUpdated = (bookingId: string, status: string) => {
    setBookingsBySession((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([id, list]) => [id, list.map((b) => (b._id === bookingId ? { ...b, bookingStatus: status } : b))]),
      ),
    );
    setLocalRefresh((n) => n + 1);
  };

  // ── Week: session card ─────────────────────────────────────────────────────
  const renderSessionCard = (s: ScheduleSession) => {
    const t = classColorTokens(colorOf(s));
    const cancelled = s.isLive === false;
    const selected = selectedSessionId === s._id;
    return (
      <Box
        key={s._id}
        component="button"
        type="button"
        onClick={() => openSession(s._id)}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          p: '10px 12px',
          borderRadius: '12px',
          border: 0,
          backgroundColor: cancelled ? SCHED.cancelledTint : t.bg,
          outline: selected ? `2px solid ${SCHED.ink}` : 'none',
          outlineOffset: '-2px',
          textAlign: 'left',
          color: SCHED.ink,
          fontFamily: SCHED.font,
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: cancelled ? SCHED.track : t.bgHover },
          '&:focus-visible': { outline: `2px solid ${SCHED.ink}` },
        }}
      >
        <Box component="span" sx={{ fontSize: '13px', fontWeight: 700, textDecoration: cancelled ? 'line-through' : 'none' }}>
          {formatInTimeZone(new Date(s.date), IST_TIMEZONE, 'h:mm a')}
        </Box>
        <Box component="span" sx={{ fontSize: '13px', color: SCHED.inkSoft, wordBreak: 'break-word' }}>
          {s.className}
        </Box>
        <Box component="span" sx={{ fontSize: '12px', fontWeight: 600, color: cancelled ? SCHED.muted : t.text }}>
          {sessionStatus(s)}
        </Box>
      </Box>
    );
  };

  // ── Empty / loading states, worded for the category being shown ───────────
  const categoryWord = classFilter === 'booked' ? 'booked' : categoryFilter === 'wellness' ? 'wellness' : 'workout';
  const nothingInView = inScope.length === 0;
  const showEmpty = !loading && !error && nothingInView;
  const showLoading = loading && nothingInView;
  const periodWord = mode === 'month' ? 'month' : 'week';

  const renderEmptyState = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '8px',
        p: { xs: '32px 20px', md: '48px 24px' },
        backgroundColor: '#FFFFFF',
        border: `1px solid ${SCHED.border}`,
        borderRadius: '20px',
      }}
    >
      {showLoading ? (
        <>
          <CircularProgress size={28} sx={{ color: SCHED.muted }} />
          <Box sx={{ fontSize: '14px', color: SCHED.muted, mt: '6px' }}>Loading {categoryWord} sessions…</Box>
        </>
      ) : (
        <>
          <Box sx={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: SCHED.hover, color: SCHED.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: '4px' }}>
            <EventBusy />
          </Box>
          <Box component="h3" sx={{ m: 0, fontSize: '18px', fontWeight: 700 }}>
            No {categoryWord} sessions this {periodWord}
          </Box>
          {anchorKey !== todayKey && (
            <Button onClick={() => setAnchorKey(todayKey)} sx={{ ...btnSx, ...btnSmSx, mt: '8px' }}>
              Back to today
            </Button>
          )}
        </>
      )}
    </Box>
  );

  const renderWeek = () => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(rangeStart, i));
    const collapsed = showEmpty || showLoading;
    return (
      <>
      <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: '12px',
            alignItems: 'start',
            minWidth: 7 * 120 + 6 * 12,
          }}
        >
          {days.map((key) => {
            const isToday = key === todayKey;
            const list = sessionsByDay.get(key) || [];
            return (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  p: '12px',
                  minHeight: collapsed ? 0 : 620,
                  boxSizing: 'border-box',
                  borderRadius: '18px',
                  backgroundColor: isToday ? '#FFFFFF' : 'transparent',
                  border: `1px solid ${isToday ? SCHED.todayBorder : 'transparent'}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', p: '2px 4px 6px' }}>
                  <Box component="span" sx={{ fontSize: '13px', fontWeight: 600, color: SCHED.muted }}>
                    {fmtKey(key, 'EEE')}
                  </Box>
                  <Box component="span" sx={{ fontSize: '20px', fontWeight: 700, color: isToday ? SCHED.primaryText : SCHED.ink }}>
                    {fmtKey(key, 'd')}
                  </Box>
                  {isToday && (
                    <Box component="span" sx={{ fontSize: '12px', fontWeight: 700, color: SCHED.primaryText }}>
                      Today
                    </Box>
                  )}
                </Box>
                {list.map(renderSessionCard)}
              </Box>
            );
          })}
        </Box>
      </Box>
      {collapsed && renderEmptyState()}
      </>
    );
  };

  // ── Today's list / Day: the week as a strip of days ────────────────────────
  const renderDayStrip = () => (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(96px, 1fr))', gap: '8px' }}>
        {Array.from({ length: 7 }, (_, i) => addDays(rangeStart, i)).map((key) => {
          const selected = key === anchorKey;
          const count = (sessionsByDay.get(key) || []).filter((s) => s.isLive !== false).length;
          return (
            <Box
              key={key}
              component="button"
              type="button"
              aria-pressed={selected}
              onClick={() => setAnchorKey(key)}
              sx={{
                p: '10px 14px',
                borderRadius: '14px',
                border: `${selected ? 2 : 1}px solid ${selected ? SCHED.primary : SCHED.border}`,
                backgroundColor: selected ? SCHED.todayTint : '#FFFFFF',
                color: SCHED.ink,
                fontFamily: SCHED.font,
                textAlign: 'left',
                cursor: 'pointer',
                '&:hover': { backgroundColor: selected ? SCHED.todayTint : SCHED.hover },
                '&:focus-visible': { outline: `2px solid ${SCHED.ink}` },
              }}
            >
              <Box sx={{ fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtKey(key, 'EEE d')}</Box>
              <Box sx={{ fontSize: '12px', color: SCHED.muted, whiteSpace: 'nowrap' }}>
                {count > 0 ? `${count} ${count === 1 ? 'class' : 'classes'}` : 'No classes'}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  // ── Month: 5–6 week grid, class count per day ───────────────────────────────
  const renderMonth = () => {
    const totalDays = Math.round((keyToUTC(rangeEnd).getTime() - keyToUTC(rangeStart).getTime()) / DAY_MS) + 1;
    const days = Array.from({ length: totalDays }, (_, i) => addDays(rangeStart, i));
    return (
      <Box sx={{ backgroundColor: '#FFFFFF', border: `1px solid ${SCHED.border}`, borderRadius: '20px', p: '16px' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '8px' }}>
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <Box key={d} component="span" sx={{ fontSize: '13px', fontWeight: 600, color: SCHED.muted, p: '4px 8px' }}>
              {d}
            </Box>
          ))}
          {days.map((key) => {
            const inMonth = key.slice(0, 7) === monthKey;
            const isToday = key === todayKey;
            const count = (sessionsByDay.get(key) || []).filter((s) => s.isLive !== false).length;
            return (
              <Box
                key={key}
                component="button"
                type="button"
                onClick={() => {
                  setAnchorKey(key);
                  setMode('week');
                }}
                sx={{
                  height: { xs: 72, md: 96 },
                  p: '10px',
                  borderRadius: '12px',
                  border: `1px solid ${SCHED.track}`,
                  backgroundColor: isToday ? SCHED.todayTint : '#FFFFFF',
                  color: inMonth ? SCHED.ink : SCHED.muted,
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  fontFamily: SCHED.font,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s',
                  '&:hover': { backgroundColor: isToday ? SCHED.todayTint : SCHED.hover },
                  '&:focus-visible': { outline: `2px solid ${SCHED.ink}` },
                }}
              >
                <Box component="span" sx={{ fontSize: '15px', fontWeight: 700 }}>{fmtKey(key, 'd')}</Box>
                <Box component="span" sx={{ fontSize: '12px', fontWeight: 600 }}>
                  {count > 0 ? `${count} ${count === 1 ? 'class' : 'classes'}` : ''}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ fontFamily: SCHED.font, color: SCHED.ink, display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <IconButton onClick={() => shift(-1)} aria-label={`Previous ${mode}`} sx={iconBtnSx(40)}>
            <ChevronLeft sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton onClick={() => shift(1)} aria-label={`Next ${mode}`} sx={iconBtnSx(40)}>
            <ChevronRight sx={{ fontSize: 20 }} />
          </IconButton>
          <Box component="span" sx={{ fontSize: '18px', fontWeight: 700, ml: '8px' }}>{title}</Box>
          <Box component="span" sx={{ fontSize: '14px', color: SCHED.muted, ml: '6px' }}>
            {inScope.length} {inScope.length === 1 ? 'session' : 'sessions'}
            {totalSlots > 0 ? ` · ${fillPct}% full` : ''}
          </Box>
          {anchorKey !== todayKey && (
            <Button onClick={() => setAnchorKey(todayKey)} sx={{ ...btnSx, ...btnSmSx, ml: '6px' }}>
              Today
            </Button>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={() => setLocalRefresh((n) => n + 1)} disabled={loading} sx={{ color: SCHED.muted }}>
                {loading ? <CircularProgress size={16} sx={{ color: SCHED.muted }} /> : <Refresh sx={{ fontSize: 18 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {/* Class filter + view switch — pinned to the top-right corner */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', ml: 'auto' }}>
          <ScheduleClassFilter
            value={classFilter}
            onChange={setClassFilter}
            options={classOptions}
            allCount={periodSessions.length}
            bookedCount={periodSessions.filter(hasBooking).length}
            periodWord={periodWord}
          />
          <Box role="group" aria-label="View" sx={{ ...segSx, flexShrink: 0 }}>
            {(['today', 'day', 'week', 'month'] as const).map((m) => (
              <Box key={m} component="button" type="button" aria-pressed={mode === m} onClick={() => setMode(m)} sx={segBtnSx(mode === m)}>
                {MODE_LABELS[m]}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {mode === 'week' ? (
        renderWeek()
      ) : mode === 'month' ? (
        showEmpty || showLoading ? renderEmptyState() : renderMonth()
      ) : (
        <>
          {renderDayStrip()}
          {showLoading ? (
            renderEmptyState()
          ) : mode === 'day' ? (
            <ScheduleDayView
              isToday={anchorKey === todayKey}
              dateLabel={`${anchorKey === todayKey ? 'Today, ' : ''}${fmtKey(anchorKey, 'EEEE d MMMM')}`}
              sessions={daySessions}
              bookingsBySession={bookingsBySession}
              waitingBySession={waitingBySession}
              notes={notes}
              now={now}
              selectedSessionId={selectedSessionId}
              colorOf={colorOf}
              onOpenSession={openSession}
              onShiftDay={(dir) => setAnchorKey((k) => addDays(k, dir))}
              onAddSessions={onAddSessions}
              emptyLabel={`No ${categoryWord} sessions on this day`}
            />
          ) : (
            <ScheduleTodayList
              dayKey={anchorKey}
              isToday={anchorKey === todayKey}
              sessions={daySessions}
              bookingsBySession={bookingsBySession}
              waitingBySession={waitingBySession}
              loading={bookingsLoading}
              now={now}
              canDownload={!isInstructor}
              notes={notes}
              colorOf={colorOf}
              onOpenSession={openSession}
              onBookingUpdated={handleBookingUpdated}
              onBookingsChanged={() => setLocalRefresh((n) => n + 1)}
              onError={setError}
            />
          )}
        </>
      )}

      <SessionBookingsDrawer
        session={selectedSession}
        color={selectedSession ? colorOf(selectedSession) : SCHED.primary}
        canWriteSessions={canWriteSessions}
        onClose={() => setSelectedSessionId(null)}
        onChanged={() => setLocalRefresh((n) => n + 1)}
        onEditSession={setSessionToEdit}
        onDeleteSession={(s) => {
          setSelectedSessionId(null);
          setSessionToDelete(s);
        }}
      />

      <UserNotePopover
        anchorEl={noteTarget?.anchorEl ?? null}
        user={noteTarget}
        studioId={noteTarget?.studioId ?? null}
        studioLabel={noteTarget?.studioLabel}
        notes={noteTarget ? notesByUser[noteTarget._id] || [] : []}
        mode={notesMode}
        onClose={() => setNoteTarget(null)}
        onChange={updateNote}
      />

      {sessionToEdit && (
        <SessionFormDialog
          open
          onClose={() => setSessionToEdit(null)}
          onSuccess={() => {
            setSessionToEdit(null);
            afterChange();
          }}
          session={sessionToEdit}
          // The session's OWN location, so under "All locations" the class
          // and instructor lists are the ones of the branch it is at.
          studioId={studioIdOf((sessionToEdit as any).studio) || studioId}
        />
      )}

      <DeleteSessionDialog
        session={sessionToDelete}
        onClose={() => setSessionToDelete(null)}
        onDeleted={() => {
          setSessionToDelete(null);
          afterChange();
        }}
        onError={setError}
      />
    </Box>
  );
};

export default ScheduleCalendarView;
